<ng-template #pageActionBarLeft>
  <bread-crum class="tpo-bread-crumb"></bread-crum>
</ng-template>

<ng-template #mainContent>
  <div class="broker-support" [attr.data-view]="currentView">

    <!-- ======================================================================
         Landing view (/tpo/support):
           News & Updates carousel + two large tile buttons sending the broker
           into the dedicated Support or Resources sub-pages.
         ====================================================================== -->
    <ng-container *ngIf="currentView === 'landing'">
      <div class="landing-grid">
        <div class="card mb-4 landing-news" *ngIf="hasNewsContent">
          <div class="card-body">
            <div class="section-title">{{ config.slideshowTitle || 'News &amp; Updates' }}</div>
            <ngb-carousel *ngIf="hasSlides" [interval]="config.slideshowCadenceSeconds * 1000"
              [showNavigationArrows]="true" [showNavigationIndicators]="true">
              <ng-template ngbSlide *ngFor="let slide of config.slides">
                <div class="slide-wrapper">
                  <a *ngIf="slide.linkUrl" [href]="slide.linkUrl"
                    [target]="slide.openInNewTab ? '_blank' : '_self'" rel="noopener noreferrer">
                    <img class="slide-image" [src]="slide.imageUrl | safeUrl" [alt]="slide.caption" />
                  </a>
                  <img *ngIf="!slide.linkUrl" class="slide-image"
                    [src]="slide.imageUrl | safeUrl" [alt]="slide.caption" />
                  <div class="slide-caption" *ngIf="slide.caption">{{ slide.caption }}</div>
                </div>
              </ng-template>
            </ngb-carousel>
          </div>
        </div>

        <div class="landing-tiles">
          <button type="button" class="landing-tile landing-tile-help" (click)="goToHelp()">
            <span class="landing-tile-icon-wrap">
              <i class="fa-solid fa-life-ring landing-tile-icon"></i>
            </span>
            <span class="landing-tile-text">
              <span class="landing-tile-label">Support</span>
              <span class="landing-tile-sub">Submit a ticket and track your cases</span>
            </span>
            <i class="fa-solid fa-arrow-right landing-tile-arrow"></i>
          </button>
          <button type="button" class="landing-tile landing-tile-resources"
            *ngIf="hasResourceCenterContent" (click)="goToResources()">
            <span class="landing-tile-icon-wrap">
              <i class="fa-solid fa-book-open landing-tile-icon"></i>
            </span>
            <span class="landing-tile-text">
              <span class="landing-tile-label">Resources</span>
              <span class="landing-tile-sub">Browse FAQs, forms and guides</span>
            </span>
            <i class="fa-solid fa-arrow-right landing-tile-arrow"></i>
          </button>
        </div>

        <!-- Guideline Guru lives on the landing page so the broker can search
             AI guidelines without having to enter the Support workflow. -->
        <div class="card mt-4 landing-guru" *ngIf="guidelineGuruEnabled">
          <div class="card-body">
            <div class="section-title">Guideline Guru</div>
            <iframe *ngIf="guidelineGuruUrl" class="guideline-guru-frame"
              [src]="guidelineGuruUrl | safeUrl"></iframe>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ======================================================================
         Help view (/tpo/support/help):
           Submit Ticket -> My Cases -> Chatbot. Each card is *ngIf'd so a
           tenant with one of them disabled just doesn't render that block.
         ====================================================================== -->
    <ng-container *ngIf="currentView === 'help'">
      <div class="view-back-row mb-2">
        <a class="back-link" (click)="goToLanding()">
          <i class="fa-solid fa-arrow-left me-2"></i>Back to Total Support
        </a>
      </div>

      <div class="help-stack">
        <!-- Submit a Support Ticket (tabs instead of accordion) -->
        <div class="card mb-4" *ngIf="hasSubmitTicketContent">
          <div class="card-body">
            <div class="section-title">Submit a Support Ticket</div>

            <div class="support-ticket-tabs">
              <button *ngFor="let entry of enabledSupportPanels; let ti = index; trackBy: trackByOriginalIndex"
                type="button" class="support-ticket-tab"
                [class.active]="selectedSupportPanelTabIndex === ti"
                (click)="selectSupportPanelTab(ti)">
                <span class="support-ticket-tab-title">{{ entry.panel.title }}</span>
                <span class="published-sla-text" *ngIf="entry.panel.publishedSla">{{ entry.panel.publishedSla }}</span>
              </button>
            </div>

            <ng-container *ngFor="let entry of enabledSupportPanels; let ti = index; trackBy: trackByOriginalIndex">
              <div class="support-ticket-body" *ngIf="selectedSupportPanelTabIndex === ti">
                <div class="support-intro" *ngIf="entry.panel.description">
                  <div class="text-muted support-description" [innerHTML]="entry.panel.description"></div>
                </div>
                <form *ngIf="!entry.panel.isInformationalOnly" #panelForm="ngForm"
                  (ngSubmit)="submitPanel(entry.originalIndex, entry.panel, panelForm)">
                  <div class="row g-3">
                    <ng-container *ngFor="let field of entry.panel.fields; let fi = index">
                      <!-- Text input -->
                      <div *ngIf="field.type === 'text'"
                        [ngClass]="field.label.toLowerCase() === 'subject' ? 'col-12' : 'col-md-6'">
                        <label class="form-label">{{ field.label }}</label>
                        <input class="form-control"
                          [name]="'panel' + entry.originalIndex + '_field' + fi"
                          [(ngModel)]="panelFormValues[entry.originalIndex][field.label]"
                          [required]="field.required" />
                      </div>
                      <!-- Textarea -->
                      <div *ngIf="field.type === 'textarea'" class="col-12">
                        <label class="form-label">{{ field.label }}</label>
                        <textarea class="form-control" rows="4"
                          [name]="'panel' + entry.originalIndex + '_field' + fi"
                          [(ngModel)]="panelFormValues[entry.originalIndex][field.label]"
                          [required]="field.required"></textarea>
                      </div>
                      <!-- Select / Dropdown -->
                      <div *ngIf="field.type === 'select'" class="col-md-6">
                        <label class="form-label">{{ field.label }}</label>
                        <select class="form-select"
                          [name]="'panel' + entry.originalIndex + '_field' + fi"
                          [(ngModel)]="panelFormValues[entry.originalIndex][field.label]"
                          [required]="field.required">
                          <option *ngFor="let opt of getFieldSelectOptions(field)" [value]="opt.value">{{ opt.label }}</option>
                        </select>
                      </div>
                      <!-- Checkbox -->
                      <div *ngIf="field.type === 'checkbox'" class="col-md-6 d-flex align-items-center">
                        <div class="form-check form-switch">
                          <input class="form-check-input" type="checkbox"
                            [name]="'panel' + entry.originalIndex + '_field' + fi"
                            [(ngModel)]="panelFormValues[entry.originalIndex][field.label]" />
                          <label class="form-check-label">{{ field.label }}</label>
                        </div>
                      </div>
                    </ng-container>
                    <!-- Attachment upload area -->
                    <div class="col-12">
                      <div class="attachment-drop-area" (click)="panelFileInput.click()"
                        (dragenter)="$event.preventDefault(); $any($event.currentTarget).classList.add('drag-over')"
                        (dragover)="$event.preventDefault()"
                        (dragleave)="$any($event.currentTarget).classList.remove('drag-over')"
                        (drop)="$event.preventDefault(); $any($event.currentTarget).classList.remove('drag-over'); handlePanelFileSelect($event, entry.originalIndex)">
                        <i class="fa-solid fa-cloud-arrow-up"></i>
                        <span>Drag files here or <strong>click to browse</strong></span>
                      </div>
                      <input type="file" hidden #panelFileInput multiple
                        (change)="handlePanelFileSelect($event, entry.originalIndex)" />
                      <div class="attachment-list" *ngIf="getPanelAttachments(entry.originalIndex).length > 0">
                        <div class="attachment-chip" *ngFor="let att of getPanelAttachments(entry.originalIndex); let ai = index">
                          <i class="fa-solid fa-paperclip"></i>
                          <span class="attachment-name">{{ att.file.name }}</span>
                          <span class="attachment-size">({{ formatFileSize(att.file.size) }})</span>
                          <button type="button" class="attachment-remove"
                            (click)="removePanelAttachment(entry.originalIndex, ai)">
                            <i class="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="mt-3 d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                      <label class="form-label mb-0 fw-semibold" style="font-size: 0.88rem;">Priority:</label>
                      <select class="form-select form-select-sm" style="width: auto; min-width: 110px;"
                        [name]="'panel' + entry.originalIndex + '_priority'"
                        [(ngModel)]="panelPriority[entry.originalIndex]">
                        <option *ngFor="let p of brokerPriorities" [value]="p">{{ p }}</option>
                      </select>
                    </div>
                    <button class="btn btn-primary" type="submit">Submit Ticket</button>
                  </div>
                </form>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- My Cases -->
        <div class="card mb-4" *ngIf="hasMyCases">
          <div class="card-body">
            <div class="section-title">My Cases</div>
            <div class="cases-filter-bar">
              <div class="cases-status-tabs">
                <button *ngFor="let status of caseStatuses" class="cases-status-tab"
                  [class.active]="caseStatusFilter === status"
                  (click)="caseStatusFilter = status">
                  {{ status }}
                  <span class="cases-status-count">{{ getCaseStatusCount(status) }}</span>
                </button>
              </div>
              <div class="cases-search-box">
                <i class="fa-solid fa-search cases-search-icon"></i>
                <input type="text" class="form-control form-control-sm" placeholder="Search cases..."
                  [(ngModel)]="caseSearchTerm" name="caseSearchTerm" />
              </div>
            </div>
            <div class="cases-list">
              <div class="empty-state" *ngIf="filteredCases.length === 0">No cases match your filters.</div>
              <div class="case-row clickable" *ngFor="let caseItem of filteredCases" (click)="openCaseDrawer(caseItem)">
                <div class="case-row-header">
                  <span class="fw-semibold">{{ caseItem.subject }}</span>
                  <span class="status-badge" [ngClass]="getStatusClass(caseItem.status)">{{ caseItem.status }}</span>
                </div>
                <div class="case-row-meta text-muted">
                  <span>{{ caseItem.caseNumber }}</span>
                  <span>·</span>
                  <span>{{ caseItem.category }}</span>
                  <span>·</span>
                  <span>{{ caseItem.dateInserted | date:'MMM d, y' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Ask the AI Chatbot -->
        <div class="card mb-4" *ngIf="hasChatbotContent">
          <div class="card-body chatbot-section">
            <div class="chatbot-header">
              <div class="chatbot-headline">{{ config.chatbotHeadline }}</div>
              <div class="chatbot-subtext">{{ config.chatbotSubText }}</div>
            </div>

            <div class="chatbot-program-tabs">
              <button *ngFor="let prog of chatbotPrograms" class="program-tab"
                [class.active]="selectedChatbotProgram?.value === prog.value"
                (click)="selectChatbotProgram(prog)">
                {{ prog.label }}
              </button>
            </div>

            <div class="chatbot-input-area">
              <textarea class="form-control chatbot-textarea" rows="4"
                [placeholder]="chatbotPlaceholder"
                [(ngModel)]="chatbotQuestion" name="chatbotQuestion"
                (keydown.enter)="$event.ctrlKey && askChatbot()"></textarea>

              <div class="chatbot-actions">
                <button class="btn btn-ask" (click)="askChatbot()"
                  [disabled]="!chatbotQuestion.trim() || !selectedChatbotProgram || chatbotLoading">
                  <span *ngIf="!chatbotLoading">Ask <i class="fa-solid fa-arrow-up-right-from-square ms-1"></i></span>
                  <span *ngIf="chatbotLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i> Searching...</span>
                </button>
                <span class="chatbot-hint text-muted">Questions will be answered in a new tab</span>
              </div>

              <div class="chatbot-overlays">
                <label class="form-check">
                  <input type="checkbox" class="form-check-input" [(ngModel)]="chatbotSearchWithOverlays" name="chatbotSearchWithOverlays" />
                  <span class="form-check-label">Search with Overlays</span>
                </label>
              </div>
            </div>

            <div class="chatbot-references" *ngIf="chatbotReferences.length > 0">
              <span class="references-label">Referencing</span>
              <a *ngFor="let ref of chatbotReferences" class="reference-chip" [href]="ref.url" target="_blank" rel="noopener noreferrer">
                {{ ref.label }} <i class="fa-solid fa-chevron-down ms-1"></i>
              </a>
            </div>

            <div class="chatbot-disclaimer" *ngIf="config.chatbotDisclaimer">
              {{ config.chatbotDisclaimer }}
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ======================================================================
         Resources view (/tpo/support/resources[/:slug]):
           Initial state is full-width tree + search. When the broker clicks
           a hosted article the tree slides into a left rail (col-3) and the
           article preview renders inline on the right (col-9).
         ====================================================================== -->
    <ng-container *ngIf="currentView === 'resources'">
      <div class="view-back-row mb-2">
        <a class="back-link" (click)="goToLanding()">
          <i class="fa-solid fa-arrow-left me-2"></i>Back to Total Support
        </a>
      </div>

      <div class="row support-grid resources-grid" [class.with-preview]="hasResourcesPreview">
        <div class="resource-rail-col"
          [ngClass]="hasResourcesPreview ? 'col-lg-3 col-12' : 'col-12'">
          <div class="card resource-rail h-100" *ngIf="hasResourceCenterContent">
            <div class="card-body">
              <div class="resource-rail-header">
                <div class="section-title mb-2">Resource Center</div>
                <div class="resource-search" *ngIf="hasResourceGroups">
                  <i class="fa-solid fa-search resource-search-icon"></i>
                  <input type="text" class="form-control form-control-sm resource-search-input"
                    placeholder="Search resources..."
                    [(ngModel)]="resourceSearchTerm" name="resourceSearchTerm" />
                  <button *ngIf="resourceSearchTerm" type="button" class="resource-search-clear"
                    (click)="clearResourceSearch()" aria-label="Clear search">
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </div>

              <div class="resource-tree" *ngIf="hasResourceGroups"
                [class.tree-fullwidth]="!hasResourcesPreview">
                <ng-container *ngFor="let group of filteredResourceGroups; let gi = index">
                  <div class="resource-group-block">
                    <div class="resource-group-name" *ngIf="group.name && group.name.length > 0">
                      <i class="fa-solid fa-folder-open me-2"></i>{{ group.name }}
                    </div>
                    <div class="resource-categories-static" *ngFor="let cat of group.categories">
                      <div class="resource-category-name">
                        <i class="fa-solid fa-folder me-2"></i>{{ cat.name }}
                      </div>
                      <div class="article-list-static" *ngIf="cat.articles.length > 0">
                        <ng-container *ngFor="let article of cat.articles">
                          <a *ngIf="article.linkType !== 'Hosted'" class="article-link"
                            [href]="article.url"
                            [target]="article.openInNewTab ? '_blank' : '_self'"
                            rel="noopener noreferrer">
                            <i class="fa-solid fa-file-alt article-icon"></i>
                            <span>{{ article.title }}</span>
                            <i class="fa-solid fa-arrow-up-right-from-square article-external"></i>
                          </a>
                          <button *ngIf="article.linkType === 'Hosted'" type="button"
                            class="article-link article-link-button"
                            [class.active]="selectedArticle?.slug === article.slug"
                            (click)="selectArticle(article)">
                            <i class="fa-solid fa-file-alt article-icon"></i>
                            <span>{{ article.title }}</span>
                            <i class="fa-solid fa-chevron-right article-external"></i>
                          </button>
                        </ng-container>
                      </div>
                    </div>
                  </div>
                </ng-container>
                <div class="empty-state" *ngIf="filteredResourceGroups.length === 0">
                  No resources match "{{ resourceSearchTerm }}".
                </div>
              </div>

              <div class="link-grid" *ngIf="!hasResourceGroups && resourceCenterLinks.length > 0">
                <a *ngFor="let link of resourceCenterLinks" class="link-tile"
                  [href]="link.url"
                  [target]="link.openInNewTab ? '_blank' : '_self'"
                  rel="noopener noreferrer">
                  {{ link.label }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-9 col-12 article-preview-col" *ngIf="hasResourcesPreview">
          <div class="card article-preview">
            <div class="card-body">
              <div class="article-preview-toolbar">
                <button type="button" class="btn btn-link p-0 back-link" (click)="closeArticlePreview()">
                  <i class="fa-solid fa-arrow-left me-1"></i> Back to all resources
                </button>
              </div>
              <ng-container *ngIf="selectedArticle && !selectedArticleNotFound">
                <h2 class="article-preview-title">{{ selectedArticle.title }}</h2>
                <div *ngIf="selectedArticleHasBody" class="article-preview-body" [innerHTML]="selectedArticleHtml"></div>
                <div *ngIf="!selectedArticleHasBody" class="article-preview-empty">
                  <i class="fa-solid fa-file-lines fa-2x mb-2 d-block"></i>
                  This article doesn't have any content yet.
                </div>
              </ng-container>
              <div *ngIf="selectedArticleNotFound" class="alert alert-warning">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>
                The requested article was not found.
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <drawer [name]="'caseDetailDrawer'" [options]="caseDrawerOptions"
      [templateRef]="caseDetailDrawerContent" (closed)="selectedCase = null">
    </drawer>

    <ng-template #caseDetailDrawerContent>
      <div class="case-drawer" *ngIf="selectedCase">
        <div class="case-drawer-meta">
          <div class="case-drawer-meta-row">
            <span class="meta-label">Case #</span>
            <span class="meta-value">{{ selectedCase.caseNumber }}</span>
          </div>
          <div class="case-drawer-meta-row">
            <span class="meta-label">Category</span>
            <span class="meta-value">{{ selectedCase.category }}</span>
          </div>
          <div class="case-drawer-meta-row">
            <span class="meta-label">Status</span>
            <span class="status-badge" [ngClass]="getStatusClass(selectedCase.status)">{{ selectedCase.status }}</span>
          </div>
          <div class="case-drawer-meta-row">
            <span class="meta-label">Opened</span>
            <span class="meta-value">{{ selectedCase.dateInserted | date:'MMM d, y h:mm a' }}</span>
          </div>
        </div>

        <div class="case-drawer-subject">
          {{ selectedCase.subject }}
        </div>

        <div class="case-drawer-thread">
          <div *ngFor="let msg of selectedCase.messages"
            class="chat-bubble"
            [ngClass]="msg.direction === 'Inbound' ? 'outbound' : 'inbound'">
            <div class="bubble-sender">{{ msg.senderName }}</div>
            <div class="bubble-body">{{ msg.body }}</div>
            <div class="bubble-attachments" *ngIf="msg.attachments?.length > 0">
              <div class="bubble-attachment" *ngFor="let att of msg.attachments" (click)="downloadAttachment(att)">
                <i class="fa-solid fa-paperclip"></i>
                <span class="bubble-attachment-name">{{ att.fileName }}</span>
                <span class="bubble-attachment-size" *ngIf="att.fileSize">({{ formatFileSize(att.fileSize) }})</span>
                <i class="fa-solid fa-download ms-1"></i>
              </div>
            </div>
            <div class="bubble-time">{{ msg.dateInserted | date:'MMM d, y, h:mm a' }}</div>
          </div>
        </div>

        <div class="case-drawer-reply">
          <textarea class="form-control" rows="3" placeholder="Type your reply... (paste a screenshot to attach)"
            [(ngModel)]="replyMessage" name="replyMessage" (keydown.enter)="$event.ctrlKey && sendReply()"
            (paste)="handleCaseReplyPaste($event)"></textarea>
          <div class="reply-attachments" *ngIf="caseReplyAttachments.length > 0">
            <div class="reply-attachment-chip" *ngFor="let att of caseReplyAttachments; let i = index">
              <i class="fa-solid fa-paperclip"></i>
              <span class="reply-attachment-name">{{ att.file.name }}</span>
              <span class="reply-attachment-size">({{ formatFileSize(att.file.size) }})</span>
              <button type="button" class="reply-attachment-remove" (click)="removeCaseReplyAttachment(i)">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
          <div class="reply-actions">
            <div class="reply-buttons">
              <button class="btn btn-outline-secondary btn-sm" type="button" (click)="caseReplyFileInput.click()">
                <i class="fa-solid fa-paperclip me-1"></i> Attach
              </button>
              <input type="file" hidden #caseReplyFileInput multiple (change)="handleCaseReplyFileSelect($event)" />
              <button class="btn btn-primary btn-sm" type="button" (click)="sendReply()" [disabled]="!replyMessage.trim()">
                <i class="fa-solid fa-paper-plane me-1"></i> Send Reply
              </button>
            </div>
          </div>
        </div>
      </div>
    </ng-template>

  </div>
</ng-template>

<tpo-page-template [pageMainContentTemplate]="mainContent" [pageActionBarLeftContentTemplate]="pageActionBarLeft"></tpo-page-template>
